"use client";

import React from "react";
var __jsx = React.createElement;
import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import APP_CONFIGURATION from '@lib/app.config';
import Icons from '../../../icons';
import { cn } from '@lib/cn';
import { useMediaQuery } from 'react-responsive';
import { BREAKPOINTS } from '@lib/constants';
import { MuscleButton } from '../buttons';
import useCssModules from '@lib/hooks/use-css-modules';
import useOnClickOutside from '@lib/hooks/use-onclick-outside';
const DialogWrapper = _ref => {
  let {
    openState,
    setOpenState,
    title,
    closeable,
    panelClassName,
    children,
    onShowConfirm,
    preventBackdropClick
  } = _ref;
  const contentRef = useRef(null);
  const {
    cssModules
  } = useCssModules();
  const {
    modalContainer,
    modalTitle,
    modalXButton
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.modal) || {};
  const handleClose = async () => {
    if (onShowConfirm) {
      const shouldClose = await onShowConfirm();
      if (shouldClose) {
        setOpenState(false);
      }
    } else {
      setOpenState(false);
    }
  };
  const handleOpenChange = open => {
    if (!open && closeable && !preventBackdropClick) {
      handleClose();
    }
  };

  // Use the click outside hook - only execute when closeable is true and preventBackdropClick is false
  useOnClickOutside(contentRef, handleClose, closeable && !preventBackdropClick);
  return __jsx(AlertDialog.Root, {
    open: openState,
    onOpenChange: handleOpenChange
  }, __jsx(AlertDialog.Portal, null, __jsx(AlertDialog.Overlay, {
    className: "fixed inset-0 bg-black bg-opacity-60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-[10000]"
  }), __jsx(AlertDialog.Content, {
    ref: contentRef,
    className: cn(modalContainer, panelClassName),
    onEscapeKeyDown: e => {
      if (closeable) {
        handleClose();
      } else {
        e.preventDefault();
      }
    }
  }, closeable && __jsx("div", {
    className: "flex justify-end"
  }, __jsx(MuscleButton, {
    onClick: handleClose,
    variant: "link",
    onTouchStart: handleClose,
    className: modalXButton
  }, __jsx(Icons.xIcon, null))), __jsx("div", {
    className: "flex flex-col"
  }, __jsx("div", {
    className: "overflow-y-auto max-h-[calc(90vh-160px)]"
  }, title && __jsx(AlertDialog.Title, {
    className: cn('w-full sticky top-0 bg-white z-[100] pb-4 text-center translate-y-[-1px]', 'max-w-none', modalTitle)
  }, title), __jsx("div", {
    className: "flex flex-col items-center px-2 pb-5"
  }, children))))));
};
const BottomPanelWrapper = _ref2 => {
  let {
    setOpenState,
    title,
    closeable,
    panelClassName,
    children,
    dataTestId,
    onShowConfirm
  } = _ref2;
  const childrenRef = useRef(null);
  const panelRef = useRef(null);
  const {
    0: isVisible,
    1: setIsVisible
  } = useState(false);
  const {
    cssModules
  } = useCssModules();
  const {
    modalXButton,
    modalTitle
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.modal) || {};
  useEffect(() => {
    // Small delay to trigger animation
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);
  const handleClose = async () => {
    if (onShowConfirm) {
      const shouldClose = await onShowConfirm();
      if (!shouldClose) return;
    }
    setIsVisible(false);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setOpenState(false);
    }, 300);
  };
  useOnClickOutside(panelRef, handleClose, closeable);
  const modalContent = __jsx("div", {
    className: "relative z-[10000]"
  }, __jsx("div", {
    className: "fixed inset-0 bg-black transition-opacity duration-300 ".concat(isVisible ? 'bg-opacity-60' : 'bg-opacity-0')
  }), __jsx("div", {
    className: "fixed h-full flex items-end inset-0 z-10 w-screen overflow-y-auto"
  }, __jsx("div", {
    className: "flex h-fit items-end justify-center p-0 text-center w-full"
  }, __jsx("div", {
    ref: panelRef,
    className: cn("relative transform w-full transition-all duration-300", isVisible ? 'translate-y-0 opacity-100' : 'translate-y-[1000px] opacity-0', "rounded-t-[24px] bg-white px-4 pb-4 pt-5 text-left shadow-xl", "rounded-b-none max-h-[90vh] overflow-hidden", panelClassName)
  }, closeable && __jsx("div", {
    className: "absolute right-4 top-2 z-[100]"
  }, __jsx(MuscleButton, {
    onClick: handleClose,
    onTouchStart: handleClose,
    variant: "link",
    dataTestId: dataTestId + "-close-button",
    className: modalXButton
  }, __jsx(Icons.xIcon, null))), __jsx("div", {
    className: cn("mt-4 w-full flex justify-center text-center flex-col items-center p-2 pt-0 self-center")
  }, __jsx("h3", {
    className: cn('w-full max-w-[277px] md:max-w-[500px] text-center', modalTitle)
  }, title), __jsx("div", {
    ref: childrenRef,
    className: "mt-3 w-full flex flex-col items-center justify-center sm:mt-0 max-h-[calc(90vh-100px)] overflow-y-auto"
  }, children))))));
  if (typeof window !== 'undefined') {
    return /*#__PURE__*/createPortal(modalContent, document.body);
  }
  return modalContent;
};
const MuscleModal = _ref3 => {
  var _APP_CONFIGURATION$co;
  let {
    openState,
    setOpenState = () => {},
    onClose,
    onShowConfirm,
    title = '',
    children,
    closeable = true,
    panelClassName,
    style,
    closeButtonLight = false,
    dataTestId,
    preventBackdropClick
  } = _ref3;
  const backgroundRef = useRef(null);
  const isMobile = useMediaQuery({
    query: "(max-width: ".concat(BREAKPOINTS.md, "px)")
  });
  const mobileModalAsBottomPanel = (_APP_CONFIGURATION$co = APP_CONFIGURATION.config.modalConfig) === null || _APP_CONFIGURATION$co === void 0 ? void 0 : _APP_CONFIGURATION$co.mobileModalAsBottomPanel;
  useEffect(() => {
    if (openState) {
      setTimeout(() => {
        if (backgroundRef.current) {
          backgroundRef.current.scrollTop = 0;
        }
      }, 100);
    }
  }, [openState]);
  const WrapperComponent = isMobile && mobileModalAsBottomPanel ? BottomPanelWrapper : DialogWrapper;
  if (!openState) return null;
  return __jsx(WrapperComponent, {
    openState: openState,
    setOpenState: setOpenState || (() => {}),
    title: title,
    closeable: closeable,
    panelClassName: panelClassName,
    onShowConfirm: onShowConfirm,
    dataTestId: dataTestId,
    preventBackdropClick: preventBackdropClick
  }, children);
};
export default MuscleModal;